import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import AgentHubCreatePasswordSetupStep from './steps/AgentHubCreatePasswordSetupStep'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import AgentHubCompleteAgencyDetailsStep from './steps/AgentHubCompleteAgencyDetailsStep'
import { connect } from 'react-redux'
import { getCurrentUserId, selectLoggedIn } from 'selectors/accountSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { authLogout } from 'actions/AuthActions'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { onboardAgency, registerCustomerId } from 'actions/AgentHubActions'
import config from 'constants/config'
import AgentHubSetupCommissionPayoutStep from './steps/AgentHubSetupCommissionPayoutStep'

interface Props {
  agentId: string
  agentEmail: string
}

interface MappedStateProps {
  isLoggedIn?: boolean
  userEmail?: string
  tenant?: string
  firstName?: string
}

function AgentHubAccountSetupModal(props: Props & MappedStateProps) {
  const { agentEmail, agentId, isLoggedIn, userEmail, tenant, firstName } = props
  const appDispatch = useAppDispatch()
  const intl = useIntl()

  const welcomeBackTitle = intl.formatMessage({
    id: 'ZSjBp1ZLj2Dik8Wleb40',
    defaultMessage: 'Welcome back',
    description: 'Modal title when agent hub account setup is already complete',
  })

  const setupTitleWithName = intl.formatMessage(
    {
      id: 'MJz0ESUZ81HWSMUaXLlf',
      defaultMessage: 'Finish setting up your account, {firstName}',
      description: 'Account setup modal title with personalized greeting',
    },
    { firstName },
  )
  const setupTitleWithoutName = intl.formatMessage({
    id: 'tWJ3uxwXq7qUHPjoHhA-',
    defaultMessage: 'Finish setting up your account.',
    description: 'Account setup modal title without name on the user account settings page',
  })
  const setupTitle = firstName ? setupTitleWithName : setupTitleWithoutName

  const accountSetupComplete = useAppSelector(state => state.agentHub.agentHubAccount.account?.status === 'onboarded')
  const account = useAppSelector(state => state.agentHub.agentHubAccount.account)
  const customerId = useAppSelector(state => getCurrentUserId(state))
  const [currentStep, setCurrentStep] = useState<'login' | 'agentHubAccount' | 'commissionsPayout'>('login')

  const { resolve } = useModalElementContext()
  useEffect(() => {
    if (isLoggedIn && agentEmail !== userEmail) {
      appDispatch(authLogout())
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [agentEmail, isLoggedIn, userEmail])

  useEffect(() => {
    if (isLoggedIn && !['leagenthub', 'leagenthub_cvfr'].includes(tenant || '')) {
      appDispatch(authLogout())
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, isLoggedIn])

  useEffect(() => {
    if (isLoggedIn && accountSetupComplete) {
      // user logged in and has an account, we're ready to go - enter the app! (close the modal)
      resolve()
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, accountSetupComplete])

  const isCommissionsPayoutStepEnabled = config.AGENT_HUB_COLLECT_PAYOUT_DETAILS_ENABLED

  useEffect(() => {
    if (isLoggedIn) {
      setCurrentStep((account?.hasAgencyIdentifier && isCommissionsPayoutStepEnabled) ? 'commissionsPayout' : 'agentHubAccount')
      if (!accountSetupComplete && account?.agentId && !account?.customerId && customerId) {
        appDispatch(registerCustomerId(account.agentId, customerId))
      }
    }
  }, [isLoggedIn, isCommissionsPayoutStepEnabled, account, accountSetupComplete, customerId, appDispatch])

  const onSubmitSetup = useCallback(() => appDispatch(onboardAgency(agentId)), [agentId, appDispatch])

  return (
    <ModalBase height="full" tabletHeight="auto" dismissible={false}>
      <ModalHeader
        title={accountSetupComplete ? welcomeBackTitle : setupTitle}
        dismissible={false}
      />

      {currentStep === 'login' && <AgentHubCreatePasswordSetupStep agentEmail={agentEmail} />}
      {currentStep === 'agentHubAccount' && <AgentHubCompleteAgencyDetailsStep agentId={agentId} />}
      {currentStep === 'commissionsPayout' &&
        <AgentHubSetupCommissionPayoutStep onSubmitSetup={onSubmitSetup} onSetupLater={onSubmitSetup} />}
    </ModalBase>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    isLoggedIn: selectLoggedIn(state),
    userEmail: state.auth.account.email,
    tenant: state.auth.account.tenant,
    firstName: state.auth.account.givenName,
  }
})(AgentHubAccountSetupModal)
